import { ChatRoomMessageSearchCompass } from "@circle-react/components/Chat/MessagingV2/ChatRoomMessageSearchCompass";
import { LazyChatThreads } from "@circle-react/components/Chat/MessagingV2/ChatThreads/LazyChatThreads";
import { LazyMemberProfileRailBar } from "@circle-react/components/Chat/MessagingV2/MemberProfileRailBar/LazyMemberProfileRailBar";
import { RailBar as MessagingRailbar } from "@circle-react/components/Chat/MessagingV2/RailBar";
import { ChatSpaceSummary } from "@circle-react/components/Spaces/ChatSpace/ChatSpaceSummary";
import { RailBar as SpaceRailbar } from "@circle-react/components/Spaces/ChatSpace/RailBar";
import { useTanstackRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { useLocation } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { JSXElementConstructor, ReactNode } from "react";

import { getMessageHashFromUrl } from "@/react/components/Chat/ChatWindowV2/helpers";
import {
  UNIFIED_INBOX_CHAT_THREADS_ROOT,
  UNIFIED_INBOX_MESSAGES_ROOT,
} from "@/react/hooks/chatsV2/messaging/useMessagingPaths";
import {
  SPACE_DATE_RANGE,
  type SpaceDateRange,
} from "@/react/types/AiSummary/SpaceSummary";

import { useSmOrMdOrLgScreenMediaQuery } from "../useMediaQuery";
import { useBoolean } from "../utils/useBoolean";
import { RAILBAR_INITIAL_STATE, RAILBAR_TYPES } from "./railbarTypes";
import type { RailbarContextValue, RailbarType } from "./railbarTypes";
import { useMemberProfileRailbar } from "./useMemberProfileRailbar";

const RailbarContext = createContext<RailbarContextValue>(
  RAILBAR_INITIAL_STATE,
);
RailbarContext.displayName = "RailbarContext";

export interface RailbarContextProviderProps {
  children: ReactNode;
  isChatSpace?: boolean;
  isCourseSpace?: boolean;
  isDrawer?: boolean;
  isLiveStream?: boolean;
  isPaddedByContainer?: boolean;
  isUnifiedInbox?: boolean;
}

export const RailbarContextProvider = ({
  isChatSpace = false,
  children,
  isLiveStream = false,
  isUnifiedInbox = false,
  isCourseSpace = false,
  isDrawer = false,
  isPaddedByContainer = false,
}: RailbarContextProviderProps) => {
  const messageHashId = getMessageHashFromUrl();
  const { message_id: messageThreadId } = useTanstackRouterQueryParams();
  const [type, setType] = useState<RailbarType>(RAILBAR_INITIAL_STATE.type);
  const [shouldShowRailBar, toggleRailBar, setShowRailBar] = useBoolean(false);
  const [
    shouldShowEmbeddedRailBar,
    toggleShowEmbeddedRailBar,
    setShowEmbeddedRailBar,
  ] = useBoolean(false);

  const [parentMessage, setParentMessage] = useState<any>(
    RAILBAR_INITIAL_STATE.parentMessage,
  );
  const [chatRoom, setChatRoom] = useState<any>(RAILBAR_INITIAL_STATE.chatRoom);
  const [messageHighlightId, setMessageHighlightId] =
    useState<any>(messageThreadId);

  const pathname = useLocation({ select: location => location.pathname });
  const isDmsPage =
    pathname.startsWith("/messages") ||
    pathname.startsWith(UNIFIED_INBOX_MESSAGES_ROOT);
  const isUnifiedInboxChatThreadsPage = pathname.startsWith(
    UNIFIED_INBOX_CHAT_THREADS_ROOT,
  );

  const isSmallOrMediumDevice = useSmOrMdOrLgScreenMediaQuery();
  const isMessageSearchRailbarOpen =
    type === RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH;

  const [dateRange, setDateRange] = useState<SpaceDateRange>(
    SPACE_DATE_RANGE.LAST_7_DAYS,
  );

  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<
    string | undefined
  >();

  const {
    closeMemberProfileRailbar,
    dismissMemberProfile,
    memberProfilePublicId,
    openMemberProfileRailbar,
  } = useMemberProfileRailbar({
    setShowRailBar,
    setType,
    shouldShowRailBar,
    type,
  });

  useEffect(() => {
    setShowRailBar(!(isSmallOrMediumDevice || isUnifiedInbox));
  }, [isSmallOrMediumDevice, isUnifiedInbox, setShowRailBar]);

  const openThreadsRailbar = useCallback(
    (message: any, chatRoom: any) => {
      setParentMessage(message);
      setChatRoom(chatRoom);
      if (isMessageSearchRailbarOpen && messageThreadId) {
        setShowEmbeddedRailBar(true);
      } else {
        setShowEmbeddedRailBar(false);
        setType(RAILBAR_TYPES.CHAT_THREADS);
        setShowRailBar(true);
      }
    },
    [isMessageSearchRailbarOpen, messageThreadId],
  );

  const closeThreadsRailbar = useCallback(() => {
    setParentMessage(null);
    setChatRoom(null);
    setType(RAILBAR_TYPES.PARTICIPANTS);
    if (isSmallOrMediumDevice || isUnifiedInbox) {
      setShowRailBar(false);
    }
  }, [isSmallOrMediumDevice, isUnifiedInbox, setShowRailBar]);

  const toggleChatRoomMessageSearchRailbar = useCallback(() => {
    setType(
      type === RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH
        ? RAILBAR_TYPES.PARTICIPANTS
        : RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH,
    );
    setShowRailBar(true);
  }, [type]);

  const updateParentMessage = useCallback((message: any) => {
    setParentMessage(message);
  }, []);

  const getParticipantsRailbarType = useCallback(() => {
    if (isChatSpace) return SpaceRailbar;
    return MessagingRailbar;
  }, [isChatSpace]);

  const openSpaceSummaryRailbar = useCallback(
    (range: SpaceDateRange, messageId?: string) => {
      setDateRange(range);
      setFirstUnreadMessageId(messageId);
      setType(RAILBAR_TYPES.CHAT_SPACE_SUMMARY);
      setShowRailBar(true);
    },
    [],
  );

  const closeSpaceSummaryRailbar = useCallback(() => {
    setType(RAILBAR_TYPES.PARTICIPANTS);
    if (isSmallOrMediumDevice || isUnifiedInbox) {
      setShowRailBar(false);
    }
  }, [isSmallOrMediumDevice, isUnifiedInbox, setShowRailBar]);

  const toggleShowRailBar = useCallback(() => {
    if (shouldShowRailBar) {
      dismissMemberProfile();
    }
    toggleRailBar();
  }, [dismissMemberProfile, shouldShowRailBar, toggleRailBar]);

  const RAILBAR_COMPONENTS: Record<RailbarType, JSXElementConstructor<any>> = {
    [RAILBAR_TYPES.PARTICIPANTS]: getParticipantsRailbarType(),
    [RAILBAR_TYPES.CHAT_THREADS]: LazyChatThreads,
    [RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH]: ChatRoomMessageSearchCompass,
    [RAILBAR_TYPES.CHAT_SPACE_SUMMARY]: ChatSpaceSummary,
    [RAILBAR_TYPES.MEMBER_PROFILE]: LazyMemberProfileRailBar,
  };

  const RailbarComponent = RAILBAR_COMPONENTS[type];
  const EmbeddedRailbarComponent =
    RAILBAR_COMPONENTS[RAILBAR_TYPES.CHAT_THREADS];

  // Only where `RailbarComponent` is actually mounted: chat spaces and the
  // messaging surfaces. Everywhere else (live streams, course comments,
  // search results, drawers) keeps the profile modal.
  const canOpenMemberProfileRailbar =
    (isChatSpace || isDmsPage || isUnifiedInboxChatThreadsPage) &&
    !isDrawer &&
    !isLiveStream;
  const isEmbeddedRailBarVisible =
    shouldShowEmbeddedRailBar && isMessageSearchRailbarOpen;

  // Memoized: chat message rows (and their toolbars) consume this context, so
  // a new value identity on every provider render would re-render every row.
  const value: RailbarContextValue = useMemo(
    () => ({
      canOpenMemberProfileRailbar,
      chatRoom,
      closeMemberProfileRailbar,
      closeThreadsRailbar,
      memberProfilePublicId,
      openMemberProfileRailbar,
      isCourseSpace,
      isLiveStream,
      isUnifiedInbox,
      isUnifiedInboxChatThreadsPage,
      isChatSpace,
      isDrawer,
      isPaddedByContainer,
      messageHighlightId,
      openThreadsRailbar,
      parentMessage,
      RailbarComponent,
      EmbeddedRailbarComponent,
      setMessageHighlightId,
      shouldShowRailBar,
      toggleShowRailBar,
      type,
      updateParentMessage,
      isDmsPage,
      toggleChatRoomMessageSearchRailbar,
      isMessageSearchRailbarOpen,
      shouldShowEmbeddedRailBar: isEmbeddedRailBarVisible,
      toggleShowEmbeddedRailBar,
      messageHashId,
      dateRange,
      setDateRange,
      openSpaceSummaryRailbar,
      closeSpaceSummaryRailbar,
      firstUnreadMessageId,
    }),
    [
      canOpenMemberProfileRailbar,
      chatRoom,
      closeMemberProfileRailbar,
      closeThreadsRailbar,
      memberProfilePublicId,
      openMemberProfileRailbar,
      isCourseSpace,
      isLiveStream,
      isUnifiedInbox,
      isUnifiedInboxChatThreadsPage,
      isChatSpace,
      isDrawer,
      isPaddedByContainer,
      messageHighlightId,
      openThreadsRailbar,
      parentMessage,
      RailbarComponent,
      EmbeddedRailbarComponent,
      shouldShowRailBar,
      toggleShowRailBar,
      type,
      updateParentMessage,
      isDmsPage,
      toggleChatRoomMessageSearchRailbar,
      isMessageSearchRailbarOpen,
      isEmbeddedRailBarVisible,
      toggleShowEmbeddedRailBar,
      messageHashId,
      dateRange,
      openSpaceSummaryRailbar,
      closeSpaceSummaryRailbar,
      firstUnreadMessageId,
    ],
  );

  return (
    <RailbarContext.Provider value={value}>{children}</RailbarContext.Provider>
  );
};

export const useRailbar = () => useContext(RailbarContext);
